import React, { useState } from 'react';
import logger from '../../utils/logger';
import { parseWeekSeries, getWeekDateRange, doDateRangesOverlap } from '../../utils/timeUtils';

// --- Debug Drawer Component ---
const DebugDrawer = ({
    isOpen,
    onClose,
    courseDefinitions,
    scheduledInstances,
    selectedPeriodId,
    timePeriods,
    selectedYearForView
}) => {
    const [copySuccess, setCopySuccess] = useState(false);
    const [rawServerData, setRawServerData] = useState(null);
    const [isLoadingRawData, setIsLoadingRawData] = useState(false);
    const [rawDataError, setRawDataError] = useState(null);

    // Calculate statistics
    const yearEntries = courseDefinitions.filter(entry => entry.year === selectedYearForView);
    const selectedPeriod = timePeriods.find(p => String(p.id) === String(selectedPeriodId));

    const fetchRawServerData = async () => {
        setIsLoadingRawData(true);
        setRawDataError(null);
        try {
            // Import getScheduleEntries to fetch raw data
            const { getScheduleEntries } = await import('../../services/apiService');
            const rawData = await getScheduleEntries();
            setRawServerData(rawData);
            logger.debug('[DebugDrawer] Raw server data:', rawData);
        } catch (error) {
            logger.error('[DebugDrawer] Error fetching raw server data:', error);
            setRawDataError(error.message || 'Failed to fetch raw data');
        } finally {
            setIsLoadingRawData(false);
        }
    };

    const copyToClipboard = async () => {
        try {
            // Get all relevant entries based on current view
            let entriesToShow = [];

            if (selectedPeriodId) {
                // Period view: show all entries that match the period (scheduled + unscheduled)
                const selectedPeriod = timePeriods.find(p => String(p.id) === String(selectedPeriodId));
                if (selectedPeriod) {
                    const periodDateRange = selectedPeriod.getDateRange();
                    entriesToShow = courseDefinitions.filter(cd => {
                        // Filter by year
                        if (cd.year !== selectedYearForView) return false;

                        // Check week series overlap if available
                        if (cd.weekSeries && cd.year) {
                            const entryWeekNumbers = parseWeekSeries(cd.weekSeries);
                            if (entryWeekNumbers.length > 0) {
                                return entryWeekNumbers.some(weekNum => {
                                    const entryWeekDateRange = getWeekDateRange(cd.year, weekNum);
                                    return entryWeekDateRange && doDateRangesOverlap(periodDateRange, entryWeekDateRange);
                                });
                            }
                        }

                        // Include entries without week series as fallback
                        return true;
                    });
                }
            } else {
                // Whole year view: show all entries for the year
                entriesToShow = yearEntries;
            }

            // Format entries as text
            const entriesText = entriesToShow.map((entry, index) => {
                const isScheduled = entry.day && entry.timeStart && entry.room;
                return [
                    `${index + 1}. ${isScheduled ? '[SCHEDULED]' : '[UNSCHEDULED]'}`,
                    `ID: ${entry.id}`,
                    `Course: ${entry.course || 'N/A'}`,
                    `Teacher: ${entry.teacher || 'N/A'}`,
                    `Group: ${entry.group || 'N/A'}`,
                    `Day: ${entry.day || 'N/A'}`,
                    `Time: ${entry.timeStart || 'N/A'} - ${entry.timeEnd || 'N/A'}`,
                    `Room: ${entry.room || 'N/A'}`,
                    `Year: ${entry.year || 'N/A'}`,
                    `Week Series: ${entry.weekSeries || 'N/A'}`,
                    `Duration: ${entry.durationMinutes || 'N/A'} minutes`,
                    `Type: ${entry.type || 'N/A'}`,
                    `---`
                ].join('\n');
            }).join('\n\n');

            const scheduledCount = entriesToShow.filter(e => e.day && e.timeStart && e.room).length;
            const unscheduledCount = entriesToShow.length - scheduledCount;

            let fullText = [
                `=== SCHEDULE ENTRIES DEBUG DUMP ===`,
                `Generated: ${new Date().toISOString()}`,
                `Selected Year: ${selectedYearForView}`,
                `Selected Period: ${selectedPeriod?.name || 'Whole Year'}`,
                `Total Entries Found: ${entriesToShow.length}`,
                `  - Scheduled: ${scheduledCount}`,
                `  - Unscheduled: ${unscheduledCount}`,
                `Total Entries in Year: ${yearEntries.length}`,
                ``,
                `=== PROCESSED ENTRIES ===`,
                entriesText || 'No entries found'
            ].join('\n');

            // Add raw server data if available
            if (rawServerData) {
                fullText += [
                    ``,
                    ``,
                    `=== RAW SERVER DATA ===`,
                    `Raw entries from server: ${Array.isArray(rawServerData) ? rawServerData.length : 'N/A'}`,
                    ``,
                    JSON.stringify(rawServerData, null, 2)
                ].join('\n');
            } else {
                fullText += [
                    ``,
                    ``,
                    `=== RAW SERVER DATA ===`,
                    `Raw server data not loaded. Click "🔄 Fetch Raw Data" button first.`
                ].join('\n');
            }

            await navigator.clipboard.writeText(fullText);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            logger.error('Failed to copy to clipboard:', err);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-xl border-l border-gray-200 z-50 flex flex-col">
            {/* Header */}
            <div className="flex-none p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800">🐛 Debug Info</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                    >
                        ×
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-grow p-4 overflow-y-auto">
                {/* Statistics */}
                <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <h4 className="font-semibold text-blue-800 mb-2">📊 Statistics</h4>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Entries for {selectedYearForView}:</span>
                                <span className="font-medium text-blue-600">{yearEntries.length}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">
                                    Entries for {selectedPeriod?.name || 'Whole Year'}:
                                </span>
                                <span className="font-medium text-green-600">{scheduledInstances.length}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Unscheduled entries:</span>
                                <span className="font-medium text-orange-600">
                                    {courseDefinitions.filter(entry => !entry.day || !entry.timeStart || !entry.room).length}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Period Details */}
                    {selectedPeriod && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <h4 className="font-semibold text-green-800 mb-2">📅 Current Period</h4>
                            <div className="space-y-1 text-sm">
                                <div><span className="font-medium">Name:</span> {selectedPeriod.name}</div>
                                <div><span className="font-medium">Weeks:</span> {selectedPeriod.start_week} - {selectedPeriod.end_week}</div>
                                <div><span className="font-medium">Year:</span> {selectedPeriod.year}</div>
                                <div><span className="font-medium">Published:</span> {selectedPeriod.published ? 'Yes' : 'No'}</div>
                            </div>
                        </div>
                    )}

                    {/* Copy Section */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <h4 className="font-semibold text-gray-800 mb-2">📋 Export Data</h4>
                        <button
                            onClick={copyToClipboard}
                            className={`w-full px-4 py-2 rounded-md text-sm font-medium transition-colors ${copySuccess
                                ? 'bg-green-100 text-green-700 border border-green-300'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            disabled={copySuccess}
                        >
                            {copySuccess ? '✅ Copied!' : '📋 Copy Entries to Clipboard'}
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                            Copies all current period entries with full details as formatted text
                        </p>
                    </div>

                    {/* Data Overview */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <h4 className="font-semibold text-yellow-800 mb-2">📈 Data Overview</h4>
                        <div className="space-y-1 text-xs text-gray-600">
                            <div>• Total course definitions loaded: {courseDefinitions.length}</div>
                            <div>• Scheduled instances shown: {scheduledInstances.length}</div>
                            <div>• Available periods: {timePeriods.length}</div>
                            <div>• Current mode: {selectedPeriodId ? 'Period View' : 'Whole Year'}</div>
                        </div>
                    </div>

                    {/* Raw Server Data Section */}
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <h4 className="font-semibold text-purple-800 mb-2">🔍 Raw Server Data</h4>
                        <button
                            onClick={fetchRawServerData}
                            disabled={isLoadingRawData}
                            className={`w-full px-4 py-2 rounded-md text-sm font-medium transition-colors mb-3 ${isLoadingRawData
                                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                : 'bg-purple-600 text-white hover:bg-purple-700'
                                }`}
                        >
                            {isLoadingRawData ? '⏳ Loading...' : '🔄 Fetch Raw Data'}
                        </button>

                        {rawDataError && (
                            <div className="text-red-600 text-xs mb-2 p-2 bg-red-50 rounded border border-red-200">
                                Error: {rawDataError}
                            </div>
                        )}

                        {rawServerData && (
                            <div className="mt-2">
                                <div className="text-xs text-purple-700 mb-2">
                                    Raw entries from server: {Array.isArray(rawServerData) ? rawServerData.length : 'N/A'}
                                </div>
                                <div className="max-h-48 overflow-y-auto bg-white border border-purple-200 rounded p-2">
                                    <pre className="text-xs text-gray-800 whitespace-pre-wrap">
                                        {JSON.stringify(rawServerData, null, 2)}
                                    </pre>
                                </div>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(JSON.stringify(rawServerData, null, 2));
                                    }}
                                    className="mt-2 w-full px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
                                >
                                    📋 Copy Raw JSON
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DebugDrawer;
